/**
 * graft/A5: the Search agent dock — a floating bottom-right launcher that
 * opens a minimizable multi-turn agent card, available on EVERY page.
 *
 * Architecture (the OmniSearch-v3 pattern on the production stack): the
 * agent loop is hosted by the search service's relay ({apiVersion}/agent/*
 * — a Claude Managed Agent behind the same JWT auth as search); THIS
 * component executes the agent's tools in the browser against the same
 * endpoints the search box uses (prefix from the searchApiVersion prop —
 * ADO 289472: the dock is graft-only, so "v2" in practice):
 *   search(query)  -> POST {searchBaseUrl}{apiVersion}/search/evaluate
 *                     (the RRF ranked fast path, same auth as the search box)
 *   get_record(rN) -> GET {searchBaseUrl}{apiVersion}/record/...
 *                     (entity_lookup + expand_related — the graph reads)
 *
 * PAGE CONTEXT: every user turn is prefixed (server-side prompt, not
 * shown in the UI) with a compact snapshot of where the user is — URL
 * path, document title, breadcrumb/heading text — so "what am I looking
 * at?" and "how do I use this page?" work. The snapshot is captured at
 * ask-time from the live DOM: path + title + the first h1/h2/breadcrumb
 * text + any data-testid page markers, truncated hard at 1.2KB.
 *
 * GATING: renders when the AUTHED search surface answers — probed via
 * GET {searchBaseUrl}v1/search/suggest (200/401 = the route exists, so
 * the relay stack is deployed; 404/HTML = absent, dock stays hidden).
 * The relay shipped as real authed endpoints (/api/v1/agent/*,
 * /v1/record/*) so hosted environments no longer need the dev surface.
 * The dev builder/user SWITCHER stays gated on the dev-surface probe in
 * devContext.ts — that surface is deliberately not promoted (P1a).
 */
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useRouteMatch } from "react-router";

import { SpaInfoContext } from "helpers/globalContext/SPAInfoContext";

import type { SearchApiVersion } from "./aiSearchFlags";
import { getEntityLink, navigateEntityLink } from "./AiSearchResults";
import { type DevContext, devContextParams, loadDevContext } from "./devContext";
import { describeOpenContext } from "./pageContext";

interface ChatItem {
    role: "user" | "agent" | "activity";
    text: string;
    /** Streamed narration bubbles carry a key so the stream's `final` event
     *  can replace the trailing ones with the envelope's exact answer text. */
    key?: string;
}

interface HandleRec {
    entity: string;
    id: string;
    summary?: string;
    /** Search-hit payload (jobId etc.) — what makes job-scoped chips routable. */
    payload?: Record<string, unknown>;
}

interface AgentToolCall {
    id: string;
    name: string;
    input: {
        query?: string;
        id?: string;
        entity_type?: string;
        metric?: string;
        field?: string;
        filters?: Record<string, unknown>;
        group_by?: string[];
    };
}

/**
 * Step-4 link contract (BTNet-Search ad29b1d): the agent cites every named
 * record as `[Record Title](rec:rN)`, where rN is a handle THIS dock minted
 * while executing the search/get_record tools. parseRecLinks splits agent
 * prose into text and chip segments; the renderer resolves each handle
 * through handlesRef into the same entity link a search-result click uses.
 * Unknown handles render as plain title text — the raw markup NEVER reaches
 * the user (the pre-parse ephemeral bug: literal "[Gainer - Rear
 * Patio](rec:r20)" in the chat). Bare `rec:rN` leftovers outside the link
 * syntax are stripped (the prompt bans them; strip what slips through).
 */
export type AgentTextSegment =
    | { kind: "text"; text: string }
    | { kind: "chip"; label: string; handle: string };

/** One activity line per tool call — the same wording whether this dock ran
 *  the tool itself (ma), the server's `tool_log` arrived with the answer, or
 *  a `tool_start` event streamed in while the adk engine was still working. */
function activityLabel(tool: string, input: Record<string, unknown> | undefined): string {
    const inp = input ?? {};
    if (tool === "search" || tool === "suggest") return `${tool}("${String(inp.query ?? "")}")`;
    if (tool === "aggregate") {
        return `aggregate(${String(inp.metric ?? "count")} ${String(inp.entity_type ?? "")})`;
    }
    if (tool === "list_records") {
        return `list_records(${String(inp.entity_type ?? "")}${inp.offset ? ` offset=${String(inp.offset)}` : ""})`;
    }
    return `${tool}(${String(inp.id ?? inp.entity_type ?? inp.name ?? inp.text ?? "")})`;
}

const REC_LINK_RE = /\[([^\]\n]{1,200})\]\(rec:(r\d{1,4})\)/g;
const BARE_HANDLE_RE = /\(?\brec:r\d{1,4}\b\)?/g;

export function parseRecLinks(text: string): AgentTextSegment[] {
    const segments: AgentTextSegment[] = [];
    let last = 0;
    for (const m of text.matchAll(REC_LINK_RE)) {
        const index = m.index ?? 0;
        if (index > last) segments.push({ kind: "text", text: text.slice(last, index) });
        segments.push({ kind: "chip", label: m[1], handle: m[2] });
        last = index + m[0].length;
    }
    if (last < text.length) segments.push({ kind: "text", text: text.slice(last) });
    return segments
        .map((seg) =>
            seg.kind === "text" ? { ...seg, text: seg.text.replace(BARE_HANDLE_RE, "") } : seg,
        )
        .filter((seg) => seg.kind === "chip" || seg.text.length > 0);
}

interface RelayResponse {
    text?: string;
    tools?: AgentToolCall[];
    done?: boolean;
    seen?: string[];
    session_id?: string;
    /** Which engine served the turn (BTNet-Search graft/agent-adk): `ma` =
     *  the Managed-Agents relay (tools come back for THIS dock to execute),
     *  `adk` = the in-process engine (every tool ran server-side; `tools` is
     *  always empty). */
    engine?: "ma" | "adk";
    /** Server-side engine only: the record handles minted THIS turn, so
     *  `[Title](rec:rN)` chips resolve without this dock having run a tool.
     *  Merged into handlesRef; never replaces it (earlier turns keep theirs). */
    handles?: Record<string, { entity: string; id: string | number; summary?: string; payload?: Record<string, unknown> }>;
    /** Server-side engine only: the tools the engine ran (names + inputs +
     *  timings, never results) — rendered as the same activity lines this
     *  dock printed while executing them itself. */
    tool_log?: { tool: string; input?: Record<string, unknown>; ms?: number }[];
}

/** Compact where-am-I snapshot for the agent's context.
 *
 * Selector stability (review 1529278): path/title/h1/h2/[role='tab'] are
 * stable APIs; the class-substring selectors (pageTitle/breadcrumb) are
 * best-effort against CSS-module names and WILL quietly stop matching if
 * those components are renamed. That degradation is ACCEPTED for this
 * dev-only surface: every selector is additive context — the agent still
 * gets path+title+headings when they miss. Do not copy this pattern into
 * production code; a product version needs a real page-context contract. */
function capturePageContext(): string {
    const parts: string[] = [];
    parts.push(`path: ${window.location.pathname}${window.location.hash || ""}`);
    if (document.title) parts.push(`title: ${document.title}`);
    const heading = document.querySelector("h1, h2, [class*='pageTitle'], [class*='PageTitle']");
    const headingText = heading?.textContent?.trim();
    if (headingText) parts.push(`heading: ${headingText.slice(0, 120)}`);
    // Structured half (Alan 2026-08-31): the open record/list parsed from the
    // route into the server's entity vocabulary, so the agent can act on the
    // page directly — "open_record: bill 9001 (job: 411) — <heading>" invites
    // get_record("bill", 9001) about the exact record on screen. Unknown
    // routes contribute nothing (describeOpenContext returns []) and the
    // block stays exactly the text snapshot it always was.
    parts.push(
        ...describeOpenContext(
            window.location.pathname,
            window.location.search,
            headingText?.slice(0, 120),
        ),
    );
    const crumbs = Array.from(
        document.querySelectorAll("[class*='breadcrumb'] a, [class*='Breadcrumb'] a"),
    )
        .map((a) => a.textContent?.trim())
        .filter(Boolean)
        .slice(0, 5);
    if (crumbs.length) parts.push(`breadcrumbs: ${crumbs.join(" > ")}`);
    // Visible tab/section labels help "how does this page work" questions.
    const tabs = Array.from(document.querySelectorAll("[role='tab']"))
        .map((t) => t.textContent?.trim())
        .filter(Boolean)
        .slice(0, 8);
    if (tabs.length) parts.push(`tabs: ${tabs.join(", ")}`);
    return parts.join("\n").slice(0, 1200);
}

export function SearchAgentDock(props: {
    searchBaseUrl: string;
    getToken: () => Promise<string>;
    /**
     * ADO 289472: endpoint prefix from the AiPoweredSearchV1 builder flag.
     * The dock is a graft-only surface, so its owner (MainNavSearchAgentDock)
     * only mounts it flag-on — i.e. "v2" in practice; the prop keeps every
     * URL derivation explicit and testable.
     */
    searchApiVersion: SearchApiVersion;
}) {
    const { searchBaseUrl, getToken, searchApiVersion } = props;
    const history = useHistory();
    const match = useRouteMatch();
    const spaContext = useContext(SpaInfoContext);
    const [available, setAvailable] = useState(false);
    const [minimized, setMinimized] = useState(true);
    const [chat, setChat] = useState<ChatItem[]>([]);
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const sessionRef = useRef<string | null>(null);
    const seenRef = useRef<string[]>([]);
    const handlesRef = useRef<Record<string, HandleRec>>({});
    const handleSeq = useRef(0);
    /** Engine that served `new` for the current session: `adk` turns stream
     *  their progress through /agent/stream, `ma` turns run the tool pump. */
    const engineRef = useRef<string | null>(null);
    const streamKeySeq = useRef(0);
    /** In-flight session creation, so an open-time pre-create and the first
     *  ask never race into two sessions. */
    const sessionPromiseRef = useRef<Promise<void> | null>(null);
    const threadEndRef = useRef<HTMLDivElement | null>(null);

    // Availability probe: the dock exists only where the AUTHED surface
    // answers. GET v1/search/suggest responds 200 (valid token) or 401
    // (route mounted, token rejected/absent) wherever the promoted stack
    // is deployed; a 404 — or an SPA catch-all serving HTML with a 200 —
    // means the surface is absent and the dock stays hidden. The
    // content-type check is load-bearing: an SPA catch-all masquerades as
    // any GET endpoint with an HTML 200 (see the ephpr round-3 capture),
    // so `ok` alone is not proof the route exists.
    // (Second half of the DOUBLE gate — MainNavSearchAgentDock already
    // requires isProduction === false before mounting this at all.
    // Deliberately NOT setDevSurfaceConfirmed: that gate belongs to the
    // dev-only switcher surface, which this probe says nothing about.)
    useEffect(() => {
        const controller = new AbortController();
        void (async () => {
            try {
                const token = await getToken().catch(() => "");
                const res = await fetch(
                    `${searchBaseUrl}${searchApiVersion}/search/suggest?q=probe`,
                    {
                        signal: controller.signal,
                        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                    },
                );
                const isJson = (res.headers.get("content-type") ?? "").includes("application/json");
                const surfaceExists = (res.ok && isJson) || res.status === 401;
                if (!controller.signal.aborted && surfaceExists) {
                    setAvailable(true);
                    // Page-load warm-up: the server readies this tenant's
                    // index (the first tool call's setup) while the user is
                    // still reading the page. Fire-and-forget; 404 on an
                    // older server is fine.
                    if (res.ok) {
                        void fetch(`${searchBaseUrl}${searchApiVersion}/agent/warm`, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                            },
                            body: "{}",
                        }).catch(() => undefined);
                    }
                }
            } catch {
                /* unreachable service: dock stays hidden */
            }
        })();
        return () => controller.abort();
    }, [searchBaseUrl, searchApiVersion, getToken]);

    // v3 triage handoff: the search panel's AgentTriageBanner dispatches
    // bt:openSearchAgent with the committed query — open the dock and ask
    // in the same conversation (the banner is the only writer; the event
    // never fires in deployed envs because the banner is probe-gated).
    const askRef = useRef<(q: string) => Promise<void>>();
    useEffect(() => {
        const onSeed = (e: Event) => {
            const q = (e as CustomEvent<{ query?: string }>).detail?.query;
            if (typeof q === "string" && q.trim()) {
                setMinimized(false);
                void askRef.current?.(q);
            }
        };
        window.addEventListener("bt:openSearchAgent", onSeed);
        return () => window.removeEventListener("bt:openSearchAgent", onSeed);
    }, []);

    // Abort plumbing for the relay pump (review 1529277): minimize/unmount
    // mid-turn must stop the loop instead of leaving it issuing searches and
    // relay round-trips against an unmounted component.
    const pumpAbortRef = useRef<AbortController | null>(null);
    useEffect(
        () => () => {
            pumpAbortRef.current?.abort();
        },
        [],
    );

    const push = useCallback((item: ChatItem) => setChat((prev) => [...prev, item]), []);

    const execSearch = useCallback(
        async (query: string): Promise<string> => {
            // Tool calls take the /evaluate fast path, not the SSE stream:
            // the stream stays open through LLM intent analysis + QA (~7-10s)
            // while the ranked hits the agent needs are ready in ~1s.
            // /evaluate with the non-LLM stages returns the RRF-FUSED ranked
            // list in one JSON body — faster AND better-ordered than the
            // stream's arrival-order hit dedupe. (Profiled 2026-08-26: tool
            // execution was 66% of a 25s agent ask; this cuts it to ~1s.)
            const ctx: DevContext = loadDevContext();
            const token = await getToken().catch(() => "");
            const res = await fetch(
                `${searchBaseUrl}${searchApiVersion}/search/evaluate${devContextParams(ctx)}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({ query, stages: ["resolver", "keyword", "semantic"] }),
                },
            );
            if (!res.ok) return `search failed (${res.status})`;
            const data = await res.json();
            const ranked: Array<{
                entity: string;
                id: string | number;
                summary?: string;
                payload?: Record<string, unknown>;
            }> = Array.isArray(data?.ranked) ? data.ranked : [];
            const lines: string[] = [];
            for (const h of ranked.slice(0, 8)) {
                const handle = `r${++handleSeq.current}`;
                handlesRef.current[handle] = {
                    entity: h.entity,
                    id: String(h.id),
                    summary: h.summary,
                    payload: h.payload,
                };
                lines.push(`${handle} [${h.entity}] ${(h.summary || "(no title)").slice(0, 120)}`);
            }
            // Step-4 typo metadata: evaluate attaches corrected:"<dym>" when
            // ranked results are thin — surface it in the tool result text so
            // the model retries the corrected spelling (BTNet-Search ad29b1d).
            const corrected = typeof data?.corrected === "string" ? data.corrected : null;
            if (lines.length === 0) {
                return corrected ? `no results; did you mean "${corrected}"?` : "no results";
            }
            const body = lines.join("\n");
            return corrected ? `${body}\n(thin results; did you mean "${corrected}"?)` : body;
        },
        [searchBaseUrl, searchApiVersion, getToken],
    );

    const execGetRecord = useCallback(
        async (handle: string): Promise<string> => {
            const arg = handle.trim();
            // Two accepted shapes (server contract, BTNet-Search a62fb2e):
            //   rN         — a handle this dock minted from a prior tool result
            //   {kind}:{id} — a direct record reference (numeric id), which the
            //                 agent uses for the OPEN RECORD from the page-
            //                 context line (open_record: bill 110509317 — ...)
            //                 without a search hop. The kind is the server's
            //                 entity slug; /v1/record 404s unknown ones.
            let rec: HandleRec | undefined = handlesRef.current[arg];
            if (!rec) {
                const direct = /^([a-z0-9_-]{1,64}):(\d{1,19})$/.exec(arg);
                if (direct) rec = { entity: direct[1], id: direct[2] };
            }
            if (!rec) return `unknown handle ${handle}`;
            // devContextParams kept: inert on authed routes (server takes
            // tenancy from the JWT), still honored under AUTH_DISABLED local.
            const ctx = loadDevContext();
            const token = await getToken().catch(() => "");
            const res = await fetch(
                `${searchBaseUrl}${searchApiVersion}/record/${encodeURIComponent(rec.entity)}/${encodeURIComponent(rec.id)}${devContextParams(ctx)}`,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                },
            );
            if (!res.ok) return `get_record failed (${res.status})`;
            const data = await res.json();
            const lines: string[] = [`${rec.entity} #${rec.id}: ${rec.summary || ""}`];
            const fields = data?.record?.fields;
            if (fields) {
                for (const [k, v] of Object.entries(fields).slice(0, 12)) {
                    if (v != null && String(v).length < 200) lines.push(`  ${k}: ${v}`);
                }
            }
            const related = data?.related?.related;
            if (Array.isArray(related) && related.length) {
                lines.push("RELATED:");
                for (const r of related.slice(0, 15)) {
                    const h2 = `r${++handleSeq.current}`;
                    handlesRef.current[h2] = {
                        entity: r.ref.entity_type,
                        id: r.ref.id,
                        summary: r.summary,
                    };
                    lines.push(
                        `  ${h2} --${r.relation}(${r.direction})--> [${r.ref.entity_type}] ${(r.summary || r.ref.id).slice(0, 100)}`,
                    );
                }
            }
            return lines.join("\n");
        },
        [searchBaseUrl, searchApiVersion, getToken],
    );

    // Step-4 executor: server-side count/sum/avg/min/max. The route returns
    // errors AS FEEDBACK ({rows:[], error:"..."}) so the agent self-corrects
    // bad field names — pass the JSON through as the tool result string.
    const execAggregate = useCallback(
        async (input: AgentToolCall["input"]): Promise<string> => {
            const token = await getToken().catch(() => "");
            const res = await fetch(`${searchBaseUrl}${searchApiVersion}/agent/aggregate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    entity_type: input.entity_type ?? "",
                    metric: input.metric ?? "count",
                    field: input.field,
                    filters: input.filters,
                    group_by: input.group_by,
                }),
            });
            if (!res.ok) return `aggregate failed (${res.status})`;
            return JSON.stringify(await res.json());
        },
        [searchBaseUrl, searchApiVersion, getToken],
    );

    /**
     * Open the record behind a rec: chip — the SAME navigation a search-
     * result click performs (getEntityLink + navigateEntityLink), so chips
     * inherit every routing rule: relative links open the SearchBarRoutes
     * modal drawer, AutoSPARouting entities navigate directly, job-scoped
     * entities resolve through the hit payload's jobId.
     */
    const openRecord = useCallback(
        (handle: string) => {
            const rec = handlesRef.current[handle];
            if (!rec) return;
            const link = getEntityLink({
                entity: rec.entity,
                id: rec.id,
                score: 0,
                rerank_score: 0,
                reranker: "agent-dock",
                summary: rec.summary,
                payload: rec.payload,
            });
            if (!link) return;
            navigateEntityLink(link, history, match.url, !!spaContext.isSpa);
        },
        [history, match.url, spaContext.isSpa],
    );

    /** Agent prose -> text + clickable record chips (step-4 link contract).
     *  Unknown/unroutable handles degrade to the plain label — never markup. */
    const renderAgentText = useCallback(
        (text: string) => {
            const segments = parseRecLinks(text);
            return segments.map((seg, i) => {
                if (seg.kind === "text") return <span key={i}>{seg.text}</span>;
                const rec = handlesRef.current[seg.handle];
                const routable =
                    rec !== undefined &&
                    getEntityLink({
                        entity: rec.entity,
                        id: rec.id,
                        score: 0,
                        rerank_score: 0,
                        reranker: "agent-dock",
                        summary: rec.summary,
                        payload: rec.payload,
                    }) !== null;
                if (!routable) {
                    // Unknown handle (hallucinated / from an expired session)
                    // or a record the SPA cannot route to: show the title as
                    // plain text — the user never sees rec: markup.
                    return <span key={i}>{seg.label}</span>;
                }
                return (
                    <button
                        key={i}
                        type="button"
                        data-testid="search-agent-rec-chip"
                        data-handle={seg.handle}
                        onClick={() => openRecord(seg.handle)}
                        style={{
                            display: "inline",
                            padding: "0 2px",
                            border: "none",
                            background: "none",
                            color: "#1f4f6f",
                            fontSize: "inherit",
                            fontWeight: 600,
                            textDecoration: "underline",
                            cursor: "pointer",
                        }}
                    >
                        {seg.label}
                    </button>
                );
            });
        },
        [openRecord],
    );

    // Step-4 executor: dym + typeahead for the agent's typo-retry loop
    // (search returned nothing -> suggest -> re-search the corrected form).
    const execSuggest = useCallback(
        async (query: string): Promise<string> => {
            const token = await getToken().catch(() => "");
            const res = await fetch(
                `${searchBaseUrl}${searchApiVersion}/search/suggest?q=${encodeURIComponent(query)}`,
                { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
            );
            if (!res.ok) return `suggest failed (${res.status})`;
            return JSON.stringify(await res.json());
        },
        [searchBaseUrl, searchApiVersion, getToken],
    );

    /** Server-side engine (adk): the envelope carries the turn's handles and
     *  tool log because this dock never executed a tool. Fold them in FIRST,
     *  before the text renders, so its chips resolve. No-op on the `ma`
     *  envelope, which carries neither. */
    const absorbServerSide = useCallback(
        (resp: RelayResponse, withActivity = true) => {
            for (const [handle, rec] of Object.entries(resp.handles ?? {})) {
                if (!rec || typeof rec !== "object" || !rec.entity) continue;
                handlesRef.current[handle] = {
                    entity: String(rec.entity),
                    id: String(rec.id),
                    summary: rec.summary,
                    payload: rec.payload,
                };
                // keep the local sequence ahead of server-minted handles so a
                // later browser-executed tool (ma engine) never reuses a name
                const n = Number(handle.replace(/^r/, ""));
                if (Number.isFinite(n) && n > handleSeq.current) handleSeq.current = n;
            }
            // A streamed turn already printed each tool as its tool_start
            // event arrived; the final envelope's tool_log must not repeat them.
            if (!withActivity) return;
            for (const t of resp.tool_log ?? []) {
                if (!t?.tool) continue;
                push({ role: "activity", text: activityLabel(t.tool, t.input) });
            }
        },
        [push],
    );

    /** adk engine: one long POST to /agent/stream — server-sent progress
     *  (tool_start / tool_end / text) while the engine works, then `final`,
     *  the exact `send` envelope. Narration bubbles appear as they are
     *  produced; `final` replaces the bubbles after the last tool with the
     *  envelope's text (guards applied) so the answer is byte-identical to
     *  the non-streaming dock's. Resolves false when the deployment has no
     *  stream route (older server / relay engine): caller falls back to
     *  send + pump. */
    const streamTurn = useCallback(
        async (message: string, signal: AbortSignal): Promise<boolean> => {
            const token = await getToken().catch(() => "");
            const r = await fetch(`${searchBaseUrl}${searchApiVersion}/agent/stream`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "text/event-stream",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    session_id: sessionRef.current,
                    text: message,
                    seen: seenRef.current,
                }),
                signal,
            });
            if (r.status === 404 || r.status === 405) return false;
            if (!r.ok || !r.body) throw new Error(`agent/stream ${r.status}`);
            let trailing: string[] = []; // keys of narration bubbles since the last tool
            const onEvent = (kind: string, data: Record<string, unknown>) => {
                if (kind === "tool_start") {
                    trailing = [];
                    push({
                        role: "activity",
                        text: activityLabel(
                            String(data.tool ?? ""),
                            data.input as Record<string, unknown> | undefined,
                        ),
                    });
                } else if (kind === "tool_end") {
                    trailing = [];
                } else if (kind === "text") {
                    const t = String(data.text ?? "").trim();
                    if (!t) return;
                    const key = `s${++streamKeySeq.current}`;
                    trailing.push(key);
                    push({ role: "agent", text: t, key });
                } else if (kind === "final") {
                    const resp = data as unknown as RelayResponse;
                    absorbServerSide(resp, false);
                    seenRef.current = resp.seen || seenRef.current;
                    const finalText = resp.text?.trim() ?? "";
                    const drop = new Set(trailing);
                    setChat((prev) => {
                        const kept = prev.filter((it) => !it.key || !drop.has(it.key));
                        return finalText ? [...kept, { role: "agent", text: finalText }] : kept;
                    });
                } else if (kind === "error") {
                    setError(`relay ${String(data.status ?? "")} ${String(data.detail ?? "")}`.trim());
                }
            };
            const reader = r.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let currentEvent = "";
            for (;;) {
                if (signal.aborted) return true;
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop() ?? "";
                for (const raw of lines) {
                    const line = raw.replace(/\r$/, "");
                    if (line.startsWith("event:")) {
                        currentEvent = line.slice(6).trim();
                    } else if (line.startsWith("data:")) {
                        try {
                            onEvent(currentEvent || "text", JSON.parse(line.slice(5)) as Record<string, unknown>);
                        } catch {
                            // malformed frame: skip it, the stream goes on
                        }
                        currentEvent = "";
                    }
                }
            }
            return true;
        },
        [searchBaseUrl, searchApiVersion, getToken, absorbServerSide, push],
    );

    const pump = useCallback(
        async (first: RelayResponse, signal: AbortSignal) => {
            let resp = first;
            for (let round = 0; round < 12; round++) {
                if (signal.aborted) return; // dock closed/unmounted mid-turn
                absorbServerSide(resp);
                if (resp.text?.trim()) push({ role: "agent", text: resp.text.trim() });
                seenRef.current = resp.seen || seenRef.current;
                if (!resp.tools?.length) break;
                const results = [];
                for (const t of resp.tools) {
                    if (signal.aborted) return;
                    const label =
                        t.name === "search" || t.name === "suggest"
                            ? `${t.name}("${t.input?.query ?? ""}")`
                            : t.name === "aggregate"
                              ? `aggregate(${t.input?.metric ?? "count"} ${t.input?.entity_type ?? ""})`
                              : `${t.name}(${t.input?.id ?? ""})`;
                    push({ role: "activity", text: label });
                    let content: string;
                    try {
                        content =
                            t.name === "search"
                                ? await execSearch(String(t.input?.query ?? ""))
                                : t.name === "get_record"
                                  ? await execGetRecord(String(t.input?.id ?? ""))
                                  : t.name === "aggregate"
                                    ? await execAggregate(t.input ?? {})
                                    : t.name === "suggest"
                                      ? await execSuggest(String(t.input?.query ?? ""))
                                      : `unknown tool ${t.name}`;
                    } catch (e) {
                        content = `tool error: ${e}`;
                    }
                    results.push({ id: t.id, content });
                }
                const token = await getToken().catch(() => "");
                const r = await fetch(`${searchBaseUrl}${searchApiVersion}/agent/tools`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({
                        session_id: sessionRef.current,
                        results,
                        seen: seenRef.current,
                    }),
                    signal,
                });
                if (!r.ok) {
                    setError(`relay ${r.status}`);
                    break;
                }
                resp = (await r.json()) as RelayResponse;
            }
        },
        [
            searchBaseUrl,
            searchApiVersion,
            getToken,
            execSearch,
            execGetRecord,
            execAggregate,
            execSuggest,
            absorbServerSide,
            push,
        ],
    );

    /** Create the agent session once; concurrent callers share the same
     *  in-flight request. builder/user body fields are inert on the authed
     *  route (the server takes tenancy from the JWT); still honored under
     *  AUTH_DISABLED local, so keep sending them. */
    const ensureSession = useCallback(
        async (title: string, ctx: DevContext): Promise<void> => {
            if (sessionRef.current) return;
            if (!sessionPromiseRef.current) {
                sessionPromiseRef.current = (async () => {
                    const token = await getToken().catch(() => "");
                    const r = await fetch(`${searchBaseUrl}${searchApiVersion}/agent/new`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        },
                        body: JSON.stringify({
                            title,
                            builder: ctx.builderId || "jwt",
                            user: ctx.userId || "jwt",
                        }),
                    });
                    if (!r.ok) throw new Error(`agent/new ${r.status}`);
                    const created = (await r.json()) as RelayResponse;
                    sessionRef.current = created.session_id ?? null;
                    engineRef.current = created.engine ?? null;
                    seenRef.current = [];
                })().finally(() => {
                    sessionPromiseRef.current = null;
                });
            }
            await sessionPromiseRef.current;
        },
        [searchBaseUrl, searchApiVersion, getToken],
    );

    /** Opening the dock is the "about to ask" signal: pre-create the session
     *  and ask the server to prime the model's prompt cache for this
     *  instance, so the first question pays for neither. Both are
     *  best-effort; the first ask repeats whatever did not land. */
    const openDock = useCallback(() => {
        setMinimized(false);
        const ctx = loadDevContext();
        void ensureSession("Search agent", ctx).catch(() => undefined);
        void (async () => {
            const token = await getToken().catch(() => "");
            await fetch(`${searchBaseUrl}${searchApiVersion}/agent/warm`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ prime_model: true }),
            });
        })().catch(() => undefined);
    }, [searchBaseUrl, searchApiVersion, getToken, ensureSession]);

    const ask = useCallback(
        async (q: string) => {
            const t = q.trim();
            if (!t || busy) return;
            setError(null);
            setBusy(true);
            push({ role: "user", text: t });
            try {
                const ctx = loadDevContext();
                const token = await getToken().catch(() => "");
                const authHeaders = {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                };
                // The session is normally pre-created when the dock opens
                // (see openDock); this only pays for it when that is still
                // in flight or never ran.
                await ensureSession(t.slice(0, 60), ctx);
                // Page context rides along invisibly so "this page" questions work.
                const pageCtx = capturePageContext();
                const message = `[USER'S CURRENT PAGE — use if the question refers to it, ignore otherwise]\n${pageCtx}\n\n[QUESTION]\n${t}`;
                pumpAbortRef.current?.abort();
                const ctrl = new AbortController();
                pumpAbortRef.current = ctrl;
                // Server-side engine: stream the turn's progress. Falls back
                // to send + pump when the deployment has no stream route.
                if (engineRef.current === "adk" && (await streamTurn(message, ctrl.signal))) {
                    return;
                }
                const r = await fetch(`${searchBaseUrl}${searchApiVersion}/agent/send`, {
                    method: "POST",
                    headers: authHeaders,
                    body: JSON.stringify({
                        session_id: sessionRef.current,
                        text: message,
                        seen: seenRef.current,
                    }),
                });
                if (!r.ok) throw new Error(`agent/send ${r.status}`);
                await pump((await r.json()) as RelayResponse, ctrl.signal);
            } catch (e) {
                if ((e as Error)?.name !== "AbortError") {
                    setError(String((e as Error)?.message || e));
                }
            } finally {
                setBusy(false);
            }
        },
        [searchBaseUrl, searchApiVersion, getToken, busy, pump, push, streamTurn],
    );
    askRef.current = ask;

    useEffect(() => {
        if (!minimized) {
            threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        }
    }, [chat.length, busy, minimized]);

    if (!available) return null;

    if (minimized) {
        return (
            <button
                type="button"
                data-testid="search-agent-launcher"
                onClick={openDock}
                aria-label="Open the search agent"
                style={{
                    position: "fixed",
                    bottom: 20,
                    right: 20,
                    zIndex: 1000,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 16px",
                    borderRadius: 999,
                    border: "none",
                    background: "#1f4f6f",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
                    cursor: "pointer",
                }}
            >
                <span aria-hidden>✦</span>
                {chat.length ? "Search agent" : "Ask the search agent"}
            </button>
        );
    }

    return (
        <div
            data-testid="search-agent-dock"
            style={{
                position: "fixed",
                bottom: 20,
                right: 20,
                zIndex: 1000,
                width: 400,
                maxHeight: "68vh",
                display: "flex",
                flexDirection: "column",
                borderRadius: 12,
                overflow: "hidden",
                background: "#f8fafc",
                border: "1px solid #d7dde3",
                boxShadow: "0 10px 34px rgba(0,0,0,0.3)",
                fontFamily: "inherit",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    background: "#1f4f6f",
                    color: "#fff",
                }}
            >
                <span aria-hidden>✦</span>
                <strong style={{ fontSize: 14 }}>Search agent</strong>
                <span style={{ fontSize: 10, opacity: 0.8, textTransform: "uppercase" }}>dev</span>
                <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                    <button
                        type="button"
                        aria-label="Minimize agent"
                        onClick={() => setMinimized(true)}
                        style={{
                            background: "none",
                            border: "none",
                            color: "#fff",
                            cursor: "pointer",
                            fontSize: 16,
                        }}
                    >
                        –
                    </button>
                </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: 12, minHeight: 120 }}>
                {chat.length === 0 && !busy && (
                    <div
                        style={{
                            color: "#8a94a0",
                            fontSize: 13,
                            textAlign: "center",
                            padding: "20px 8px",
                        }}
                    >
                        Ask about your data or about the page you're on — the agent searches
                        records, follows relationships, and knows where you are.
                    </div>
                )}
                {chat.map((item, i) =>
                    item.role === "user" ? (
                        <div
                            key={i}
                            style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}
                        >
                            <div
                                style={{
                                    maxWidth: "85%",
                                    padding: "8px 12px",
                                    borderRadius: "14px 14px 4px 14px",
                                    background: "#1f4f6f",
                                    color: "#fff",
                                    fontSize: 13,
                                }}
                            >
                                {item.text}
                            </div>
                        </div>
                    ) : item.role === "activity" ? (
                        <div
                            key={i}
                            style={{ fontSize: 11, color: "#8a94a0", margin: "4px 0 4px 4px" }}
                        >
                            ⚙ {item.text}
                        </div>
                    ) : (
                        <div
                            key={i}
                            style={{
                                maxWidth: "95%",
                                padding: "8px 12px",
                                marginBottom: 8,
                                borderRadius: "14px 14px 14px 4px",
                                background: "#fff",
                                border: "1px solid #e3e8ee",
                                fontSize: 13,
                                whiteSpace: "pre-wrap",
                            }}
                        >
                            {renderAgentText(item.text)}
                        </div>
                    ),
                )}
                {busy && (
                    <div style={{ fontSize: 13, color: "#8a94a0" }}>Thinking and searching…</div>
                )}
                {error && (
                    <div style={{ fontSize: 12, color: "#c0392b", marginTop: 6 }}>{error}</div>
                )}
                <div ref={threadEndRef} />
            </div>

            <form
                style={{
                    display: "flex",
                    gap: 8,
                    padding: "8px 12px",
                    borderTop: "1px solid #e3e8ee",
                    background: "#fff",
                }}
                onSubmit={(e) => {
                    e.preventDefault();
                    if (draft.trim() && !busy) {
                        void ask(draft);
                        setDraft("");
                    }
                }}
            >
                <input
                    data-testid="search-agent-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={chat.length ? "Ask a follow-up…" : "Ask the agent…"}
                    style={{
                        flex: 1,
                        fontSize: 13,
                        padding: "6px 10px",
                        borderRadius: 6,
                        border: "1px solid #cdd6de",
                        outline: "none",
                    }}
                />
                <button
                    type="submit"
                    disabled={busy || !draft.trim()}
                    aria-label="Send"
                    style={{
                        padding: "6px 12px",
                        borderRadius: 6,
                        border: "none",
                        background: busy || !draft.trim() ? "#9db4c4" : "#1f4f6f",
                        color: "#fff",
                        cursor: busy || !draft.trim() ? "default" : "pointer",
                        fontSize: 13,
                    }}
                >
                    ➤
                </button>
            </form>
        </div>
    );
}
